<template>
  <div>
    <v-dialog
      v-model="dialog"
      scrollable
      max-width="1000"
      class="och-dialogExport"
    >
      <template v-slot:activator="{ on }">
        <slot v-bind:on="on">
          <v-btn outlined small color="primary" v-on="on"
            >Export ({{ entityList.length }})</v-btn
          >
        </slot>
      </template>

      <v-card class="px-2">
        <v-card-title primary-title>
          <v-toolbar elevation="0">
            <h1 class="mr-8 block">
              Download {{ upperFirst(entityName) }} Export
            </h1>
            <v-spacer></v-spacer>

            <v-btn icon small @click="dialog = false">
              <v-icon small>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>
        <v-divider class="mb-5"></v-divider>

        <v-card-text>
          <v-container class="pa-0" fluid>
            <div v-if="presetList.length !== 1">
              <label class="och-label mb-2"> Select a File Preset </label>
              <v-autocomplete
                @change="setPreset"
                :items="presetList"
                data-test="presetList"
                item-text="label"
                item-value="id"
                outlined
                placeholder="File Preset..."
              >
              </v-autocomplete>
            </div>

            <div v-if="preset">
              <label class="och-label mb-2">
                Choose Columns to Include in File
              </label>

              <v-row v-if="presetList.length">
                <v-col>
                  <div
                    class="data-table-override"
                    v-if="
                      Array.isArray(columnListRequired) &&
                      columnListRequired.length
                    "
                  >
                    <h3>Required Columns</h3>
                    <div v-for="column in columnListRequired" :key="column.id">
                      <v-checkbox
                        :disabled="true"
                        :input-value="true"
                        :label="column.label"
                        class="mt-0"
                        color="primary"
                        dense
                        hide-details
                      ></v-checkbox>
                    </div>
                  </div>
                  <div
                    class="data-table-override"
                    v-if="
                      Array.isArray(columnListOptional) &&
                      columnListOptional.length
                    "
                  >
                    <h3 class="mt-2">Optional Columns</h3>
                    <div class="mb-1">
                      <v-btn
                        @click="deselectAllOptions"
                        color="primary"
                        outlined
                        small
                        v-if="hasSelectedOptions"
                      >
                        Deselect All</v-btn
                      >
                      <v-btn
                        @click="selectAllOptions"
                        color="primary"
                        outlined
                        small
                        v-else
                        >Select All</v-btn
                      >
                    </div>
                    <div v-for="column in columnListOptional" :key="column.id">
                      <v-checkbox
                        :label="column.label"
                        class="mt-0"
                        color="primary"
                        dense
                        hide-details
                        v-model="columnListOptionalSelectionMap[column.name]"
                      ></v-checkbox>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row v-else>
                <v-col>
                  <p class="mt-8 text-center">No exports configured</p>
                </v-col>
              </v-row>
            </div>
          </v-container>
        </v-card-text>
        <v-divider class="mb-2"></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-form
            action="/api/entity/export-user"
            enctype="multipart/form-data"
            method="post"
          >
            <input
              :value="preset.id"
              name="presetId"
              type="hidden"
              v-if="preset"
            />
            <input :value="entityName" name="entityName" type="hidden" />
            <input
              :key="`${name}-${index}`"
              :value="name"
              name="optionalAttributeNameList[]"
              type="hidden"
              v-for="(name, index) in columnListOptionalSelectedNameList"
            />
            <input
              :key="`${entity[primaryKeyName]}-${index}`"
              :value="entity[primaryKeyName]"
              name="entityIdList[]"
              type="hidden"
              v-for="(entity, index) in entityList"
            />
            <v-btn
              :disabled="!Array.isArray(headerList) || !headerList.length"
              class="mb-2 mr-2"
              color="primary"
              type="submit"
              >Download</v-btn
            >
          </v-form>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { pickBy, get, has, keys, keyBy, upperFirst } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  computed: {
    attributeList() {
      const { entityName } = this;
      return this.attributeFilter({ entityName });
    },
    columnListOptionalSelectedNameList() {
      const { columnListOptionalSelectionMap } = this;

      const selectedNameMap = pickBy(
        columnListOptionalSelectionMap,
        isSelected => !!isSelected
      );
      const nameList = keys(selectedNameMap);

      return nameList;
    },
    hasSelectedOptions() {
      return !!this.columnListOptionalSelectedNameList.length;
    },
    headerList() {
      const { columnListOptionalSelectedNameList, columnListRequired } = this;

      const columnListRequiredNameList = columnListRequired.map(
        ({ name }) => name
      );
      const headerList = columnListRequiredNameList.concat(
        columnListOptionalSelectedNameList
      );

      return headerList;
    },
    organization() {
      if (!this.organizationId) {
        return;
      }

      return this.getOrganizationById(this.organizationId);
    },
    presetList() {
      const presetList = get(
        this.$store.state,
        `${this.entityName}Preset.list`
      );

      if (!presetList) {
        return [];
      }
      return presetList;
    },
    primaryKeyName() {
      const primaryKeyAttribute = this.attributeList.find(
        attribute => !!attribute.primaryKey
      );

      if (!primaryKeyAttribute) {
        throw new Error(
          `No primary key attribute found for ${this.entityName}`
        );
      }

      return primaryKeyAttribute.name;
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
      getOrganizationById: "organization/getById",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
    }),
  },
  async created() {
    const { entityName } = this;

    try {
      await this.$store.dispatch(`${entityName}Preset/list`, { reload: false });
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: `Unable to load ${entityName} presets` });
      return;
    }

    try {
      await Promise.all([this.attributeGetList({ reload: false })]);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Error loading data from server" });
      return;
    }

    const { presetIndex, presetList } = this;
    const preset = presetList[presetIndex];
    if (!preset) {
      return;
    }
    const { id } = preset;
    this.setPreset(id);
  },
  data() {
    return {
      attributeListSelected: [],
      columnListOptional: [],
      columnListOptionalSelectionMap: {},
      columnListRequired: [],
      columnMap: {},
      dialog: false,
      preset: null,
      presetIndex: 0,
    };
  },
  methods: {
    deselectAllOptions() {
      this.columnListOptional.forEach(({ name: optionalAttributeName }) => {
        this.$set(
          this.columnListOptionalSelectionMap,
          optionalAttributeName,
          false
        );
      });
    },
    selectAllOptions() {
      this.columnListOptional.forEach(({ name: optionalAttributeName }) => {
        this.$set(
          this.columnListOptionalSelectionMap,
          optionalAttributeName,
          true
        );
      });
    },
    setPreset(presetId) {
      if (!presetId) {
        return;
      }

      const preset = this.presetList.find(item => item.id === presetId);
      if (!preset) {
        return;
      }

      this.preset = preset;

      const { attributeList } = this;
      const attributeMapByName = keyBy(attributeList, "name");

      const columnListFilteredByCareSetting = preset.attributeList
        .map(({ name }) => attributeMapByName[name])
        .filter(attribute => {
          // If the attribute object does not have both of these properties, its care setting values are irrelevant.
          if (!has(attribute, "acute") && !has(attribute, "ambulatory")) {
            return true;
          }

          if (this.organization.careSetting === "Acute Care") {
            return !!attribute.acute;
          }

          if (this.organization.careSetting === "Ambulatory") {
            return !!attribute.ambulatory;
          }
          return true;
        });

      const columnListRequired = columnListFilteredByCareSetting.filter(
        ({ required }) => !!required
      );

      const columnListOptional = columnListFilteredByCareSetting.filter(
        ({ required }) => !required
      );

      this.columnListRequired = columnListRequired;
      this.columnListOptional = columnListOptional;
      this.columnListOptionalSelected = [];
      this.columnListOptionalSelectionMap = {};
    },
    upperFirst,
    ...mapActions({
      attributeGetList: "attribute/list",
    }),
  },
  props: {
    entityList: { default: () => [], type: Array },
    entityName: {
      require: true,
      type: String,
    },
  },
};
</script>

<style>
.data-table-override td {
  border: none !important;
}
</style>
